import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap } from '@cm/core/localization';

const pathStart = 'Catalogs|Exam|Details|';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	feature: HubFeature.Exam,
	portal: 'SP',
};

type LocNameStart = 'SPExamCatalogDetails';
export type SPExamCatalogDetailsLocNames =
	| `${LocNameStart}TitlePage`
	| `${LocNameStart}ButtonCancel`
	| `${LocNameStart}ButtonUpdate`
	| `${LocNameStart}ButtonEdit`
	| `${LocNameStart}LabelGroup`
	| `${LocNameStart}LabelName`
	| `${LocNameStart}LabelNameAbbr`
	| `${LocNameStart}LabelExternalId`
	| `${LocNameStart}LabelLiveExam`
	| `${LocNameStart}ButtonLiveExamLink`
	| `${LocNameStart}LabelStartDate`
	| `${LocNameStart}LabelEndDate`
	| `${LocNameStart}LabelValidPeriod`
	| `${LocNameStart}ContentForever`
	| `${LocNameStart}LabelCadcExclude`
	| `${LocNameStart}LabelMaxScore`
	| `${LocNameStart}LabelDisplayOrder`
	| `${LocNameStart}LabelCriterialUrl`
	| `${LocNameStart}LabelSchedulingLink`
	| `${LocNameStart}LabelShowExag`
	| `${LocNameStart}LabelDescription`
	| `${LocNameStart}LabelMaximizePoints`
	| `${LocNameStart}LabelPoints`;

export const spExamCatalogDetailsLocs: LocDefs<SPExamCatalogDetailsLocNames> = {
	SPExamCatalogDetailsTitlePage: {
		defaultValue: 'Exam Details',
		description: 'Page title',
		displayPath: `${pathStart}Page title`,
		displayType: 'Title',
		legacyName: 'Exam/Catalogs.ExamDetails.Pagetitle.ExamDetails',
		...sharedProperties,
	},
	SPExamCatalogDetailsButtonCancel: {
		defaultValue: 'Cancel',
		description: 'Cancel button',
		displayPath: `${pathStart}Cancel`,
		displayType: 'Button',
		legacyName: 'Exam/Catalogs.ExamDetails.Button.Cancel',
		...sharedProperties,
	},
	SPExamCatalogDetailsButtonUpdate: {
		defaultValue: 'Update',
		description: 'Update button',
		displayPath: `${pathStart}Update`,
		displayType: 'Button',
		legacyName: 'Exam/Catalogs.ExamDetails.Button.Update',
		...sharedProperties,
	},
	SPExamCatalogDetailsButtonEdit: {
		defaultValue: 'Edit Details',
		description: 'Edit Details button',
		displayPath: `${pathStart}Edit Details`,
		displayType: 'Button',
		legacyName: 'Exam/Catalogs.ExamDetails.Button.EditDetails',
		...sharedProperties,
	},
	SPExamCatalogDetailsLabelGroup: {
		defaultValue: 'Group',
		description: 'Label for: Group',
		displayPath: `${pathStart}Group`,
		displayType: 'Label',
		legacyName: 'Exam/Catalogs.ExamDetails.FieldLabel.Group',
		...sharedProperties,
	},
	SPExamCatalogDetailsLabelName: {
		defaultValue: 'Name',
		description: 'Label for: Name',
		displayPath: `${pathStart}Name`,
		displayType: 'Label',
		legacyName: 'Exam/Catalogs.ExamDetails.FieldLabel.Name',
		...sharedProperties,
	},
	SPExamCatalogDetailsLabelNameAbbr: {
		defaultValue: 'Abbreviation',
		description: 'Label for: Abbreviation',
		displayPath: `${pathStart}Abbreviation`,
		displayType: 'Label',
		legacyName: 'Exam/Catalogs.ExamDetails.FieldLabel.Abbreviation',
		...sharedProperties,
	},
	SPExamCatalogDetailsLabelExternalId: {
		defaultValue: 'External ID',
		description: 'Label for: External ID',
		displayPath: `${pathStart}External ID`,
		displayType: 'Label',
		legacyName: 'Exam/Catalogs.ExamDetails.FieldLabel.ExternalId',
		...sharedProperties,
	},
	SPExamCatalogDetailsLabelLiveExam: {
		defaultValue: 'Live Exam',
		description: 'Label for: Live Exam',
		displayPath: `${pathStart}Live Exam`,
		displayType: 'Label',
		legacyName: 'Exam/Catalogs.ExamDetails.FieldLabel.LiveExam',
		...sharedProperties,
	},
	SPExamCatalogDetailsButtonLiveExamLink: {
		defaultValue: 'View',
		description: 'Button to view the live exam.',
		displayPath: `${pathStart}View`,
		displayType: 'Button',
		legacyName: 'Exam/Catalogs.ExamDetails.Button.LiveExamLink',
		...sharedProperties,
	},
	SPExamCatalogDetailsLabelStartDate: {
		defaultValue: 'Start Date',
		description: 'Label for: Start Date',
		displayPath: `${pathStart}Start Date`,
		displayType: 'Label',
		legacyName: 'Exam/Catalogs.ExamDetails.FieldLabel.StartDate',
		...sharedProperties,
	},
	SPExamCatalogDetailsLabelEndDate: {
		defaultValue: 'End Date',
		description: 'Label for: End Date',
		displayPath: `${pathStart}End Date`,
		displayType: 'Label',
		legacyName: 'Exam/Catalogs.ExamDetails.FieldLabel.EndDate',
		...sharedProperties,
	},
	SPExamCatalogDetailsLabelValidPeriod: {
		defaultValue: 'Valid (In Months)',
		description: 'Label for: Valid (In Months)',
		displayPath: `${pathStart}Valid (In Months)`,
		displayType: 'Label',
		legacyName: 'Exam/Catalogs.ExamDetails.FieldLabel.ValidPeriod',
		...sharedProperties,
	},
	SPExamCatalogDetailsContentForever: {
		defaultValue: 'Forever',
		description: 'Text to display when the exam is valid forever.',
		displayPath: `${pathStart}Forever`,
		displayType: 'Content',
		legacyName: 'Exam/Catalogs.ExamDetails.Content.Forever',
		...sharedProperties,
	},
	SPExamCatalogDetailsLabelCadcExclude: {
		defaultValue: 'CADC Exclude',
		description: 'Label for: CADC Exclude.',
		displayPath: `${pathStart}CADC Exclude`,
		displayType: 'Label',
		legacyName: 'Exam/Catalogs.ExamDetails.FieldLabel.CADCExclude',
		...sharedProperties,
	},
	SPExamCatalogDetailsLabelMaxScore: {
		defaultValue: 'Maximum Score',
		description: 'Label for: Maximum Score.',
		displayPath: `${pathStart}Maximum Score`,
		displayType: 'Label',
		legacyName: 'Exam/Catalogs.ExamDetails.FieldLabel.MaxScore',
		...sharedProperties,
	},
	SPExamCatalogDetailsLabelDisplayOrder: {
		defaultValue: 'Display Order',
		description: 'Label for: Display Order.',
		displayPath: `${pathStart}Display Order`,
		displayType: 'Label',
		legacyName: 'Exam/Catalogs.ExamDetails.FieldLabel.DisplayOrder',
		...sharedProperties,
	},
	SPExamCatalogDetailsLabelCriterialUrl: {
		defaultValue: 'Criteria URL',
		description: 'Label for: Criteria URL.',
		displayPath: `${pathStart}Criteria URL`,
		displayType: 'Label',
		legacyName: 'Exam/Catalogs.ExamDetails.FieldLabel.CriteriaURL',
		...sharedProperties,
	},
	SPExamCatalogDetailsLabelSchedulingLink: {
		defaultValue: 'Scheduling Link',
		description: 'Label for: Scheduling Link.',
		displayPath: `${pathStart}Scheduling Link`,
		displayType: 'Label',
		legacyName: 'Exam/Catalogs.ExamDetails.FieldLabel.SchedulingLink',
		...sharedProperties,
	},
	SPExamCatalogDetailsLabelShowExag: {
		defaultValue: 'Show exam with altered grade',
		description: 'Label for: Show exam with altered grade',
		displayPath: `${pathStart}Show exam with altered grade`,
		displayType: 'Label',
		legacyName: 'Exam/Catalogs.ExamDetails.FieldLabel.ShowExag',
		...sharedProperties,
	},
	SPExamCatalogDetailsLabelDescription: {
		defaultValue: 'Description',
		description: 'Label for: Description',
		displayPath: `${pathStart}Description`,
		displayType: 'Label',
		legacyName: 'Exam/Catalogs.ExamDetails.FieldLabel.Description',
		...sharedProperties,
	},
	SPExamCatalogDetailsLabelMaximizePoints: {
		defaultValue: 'Maximize Points',
		description: 'Label for: Maximize Points',
		displayPath: `${pathStart}Maximize Points`,
		displayType: 'Label',
		feature: HubFeature.CredentialContinuingEducationCiscoCustomDisplayExamCatalogPoints,
		portal: 'SP',
	},
	SPExamCatalogDetailsLabelPoints: {
		defaultValue: 'Points',
		description: 'Label for: Points',
		displayPath: `${pathStart}Points`,
		displayType: 'Label',
		feature: HubFeature.CredentialContinuingEducationCiscoCustomDisplayExamCatalogPoints,
		portal: 'SP',
	},
};

export type SPExamCatalogDetailsLocType = LocMap<SPExamCatalogDetailsLocNames>;
